import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { fmtRp, errMsg, branchLabel } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { TrendingUp, ShoppingBag, Wallet, Package, AlertTriangle, ArrowLeftRight, Loader2 } from 'lucide-react';

export default function Dashboard() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [branch, setBranch] = useState(isAdmin ? '' : user.branch);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    const fetchData = () => api.get('/dashboard', { params: { branch } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
    fetchData();
    const iv = setInterval(fetchData, 15000);
    return () => clearInterval(iv);
  }, [branch]);

  const stats = data ? [
    { label: 'Penjualan Hari Ini', value: fmtRp(data.today_revenue), icon: TrendingUp, color: 'text-emerald-400 bg-emerald-500/10' },
    { label: 'Transaksi Hari Ini', value: data.today_trx, icon: ShoppingBag, color: 'text-blue-400 bg-blue-500/10' },
    { label: 'Penjualan Bulan Ini', value: fmtRp(data.month_revenue), icon: TrendingUp, color: 'text-brand bg-brand/10' },
    { label: 'Pengeluaran Hari Ini', value: fmtRp(data.today_expenses), icon: Wallet, color: 'text-amber-400 bg-amber-500/10' },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground">Ringkasan toko Anda hari ini</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2" data-testid="dashboard-branch-filter">
            <button onClick={() => setBranch('')} data-testid="dash-branch-semua"
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${branch === '' ? 'bg-brand text-white border-brand' : 'bg-surface border-line text-muted-foreground hover:border-linehi'}`}>
              Semua
            </button>
            {branches.map((b) => (
              <button key={b.id} onClick={() => setBranch(b.id)} data-testid={`dash-branch-${b.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${branch === b.id ? 'bg-brand text-white border-brand' : 'bg-surface border-line text-muted-foreground hover:border-linehi'}`}>
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
      ) : (
        <>
          {isAdmin && data.pending_transfers > 0 && (
            <Link to="/oper-stok" data-testid="pending-transfer-alert"
              className="lodi-card border-amber-500/40 p-4 flex items-center gap-3 hover:border-amber-500">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-sm font-semibold">{data.pending_transfers} permintaan oper stok menunggu persetujuan Anda →</p>
            </Link>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="lodi-card p-5" data-testid={`stat-card-${i}`} style={{ animationDelay: `${i * 60}ms` }}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{s.label}</p>
                <p className="font-display text-xl sm:text-2xl font-extrabold mt-1">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lodi-card p-5" data-testid="low-stock-panel">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="font-display font-bold">Stok Menipis ({data.low_stock_count})</h3>
              </div>
              {data.low_stock.length === 0 ? (
                <p className="text-sm text-muted-foreground">Semua stok aman.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {data.low_stock.map((l, i) => (
                    <div key={i} className="flex items-center justify-between bg-elevated rounded-lg px-3 py-2 text-sm">
                      <span>{l.article} <span className="text-muted-foreground">• Uk {l.size}</span></span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{branchLabel(l.branch)}</span>
                        <span className={`font-bold ${l.qty === 0 ? 'text-brand' : 'text-amber-400'}`}>{l.qty} psg</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lodi-card p-5" data-testid="top-products-panel">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-brand" />
                <h3 className="font-display font-bold">Terlaris Bulan Ini</h3>
              </div>
              {data.top_products.length === 0 ? (
                <p className="text-sm text-muted-foreground">Belum ada penjualan bulan ini.</p>
              ) : (
                <div className="space-y-2">
                  {data.top_products.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-elevated rounded-lg px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-md bg-brand/15 text-brand text-xs font-bold flex items-center justify-center">{i + 1}</span>
                        {t.article}
                      </span>
                      <span className="font-semibold">{t.qty} psg</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lodi-card p-5" data-testid="recent-transactions-panel">
            <h3 className="font-display font-bold mb-4">Transaksi Terbaru</h3>
            {data.recent_transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada transaksi.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line">
                      <th className="pb-2 pr-4">Invoice</th>
                      <th className="pb-2 pr-4">Tanggal</th>
                      <th className="pb-2 pr-4">Kasir</th>
                      <th className="pb-2 pr-4">Cabang</th>
                      <th className="pb-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_transactions.map((t) => (
                      <tr key={t.id} className="border-b border-line/50 last:border-0">
                        <td className="py-2.5 pr-4 font-mono text-xs">{t.invoice_no}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{t.date_wib} {t.time_wib}</td>
                        <td className="py-2.5 pr-4">{t.cashier_name}</td>
                        <td className="py-2.5 pr-4">{branchLabel(t.branch)}</td>
                        <td className="py-2.5 text-right font-semibold text-emerald-400">{fmtRp(t.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
